/**
 * GlobalView — App Shell Layout
 *
 * Main application layout (§11.1):
 * ┌──────────────────────────────────────┐
 * │  Header (56px)                       │
 * ├──────────┬───────────┬───────────────┤
 * │ Sidebar  │  3D View  │  Context      │
 * │ (280px)  │  (flex-1) │  (opt. 320px) │
 * ├──────────┴───────────┴───────────────┤
 * │  Timeline (64px)                     │
 * └──────────────────────────────────────┘
 */

import { useState, useEffect, useCallback } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import ContextPanel from './ContextPanel';
import MobileBottomNav from './MobileBottomNav';
import GlobeViewer from '../globe/GlobeViewer';
import LoadingScreen from '../ui/LoadingScreen';
import ViewportHUD from '../ui/ViewportHUD';
import AdminPage from '../../pages/AdminPage';
import { useGlobeStore } from '../../stores/globeStore';
import { useAuthStore } from '../../stores/authStore';
import { useIsMobile } from '../../hooks/useMediaQuery';
import styles from './AppShell.module.css';

export default function AppShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const globeReady = useGlobeStore((s) => s.ready);
  const role = useAuthStore((s) => s.role);
  const isMobile = useIsMobile();

  // Loading overlay lifecycle: visible → fadingOut → unmounted
  const [showLoading, setShowLoading] = useState(true);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    if (globeReady && !fadingOut) {
      const timer = setTimeout(() => setFadingOut(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [globeReady, fadingOut]);

  const handleFadeComplete = useCallback(() => {
    setShowLoading(false);
  }, []);

  return (
    <div className={`${styles.shell} ${isMobile ? styles.mobile : ''}`}>
      <Header onShowAdmin={role === 'admin' ? () => setShowAdmin(true) : undefined} />

      <div className={styles.body}>
        {/* Desktop: Sidebar + ContextPanel flanking the globe */}
        {!isMobile && (
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((c) => !c)}
          />
        )}

        <main className={styles.viewport} id="globe-viewport">
          <GlobeViewer />
          {!isMobile && <ViewportHUD />}
        </main>

        {!isMobile && <ContextPanel />}
      </div>

      {/* Desktop: Timeline bar */}
      {!isMobile && (
        <div className={styles.timeline} id="timeline-bar">
          <span className={styles.timelineLabel}>TIMELINE</span>
          <span className={styles.timelineHint}>Phase 5</span>
        </div>
      )}

      {/* Mobile: Spy cockpit bottom nav */}
      {isMobile && <MobileBottomNav onShowAdmin={role === 'admin' ? () => setShowAdmin(true) : undefined} />}

      {/* Admin overlay */}
      {showAdmin && <AdminPage onClose={() => setShowAdmin(false)} />}

      {/* Spy cockpit loading overlay — stays until globe is ready */}
      {showLoading && (
        <LoadingScreen fadingOut={fadingOut} onFadeComplete={handleFadeComplete} />
      )}
    </div>
  );
}
